import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    BarChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface VisitorOnboardingSplitData {
    period: string;
    period_start: string;
    total_visitors: number;
    anonymous_visitors: number;
    registered_visitors: number;
    profile_complete_visitors: number;
}

interface Props {
    data?: VisitorOnboardingSplitData[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const formatPeriodLabel = (date: Date, period: Props['period'], fallback: string) => {
    switch (period) {
        case 'daily':
            return format(date, 'MMM d');
        case 'weekly':
            return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
        case 'monthly':
            return format(date, 'MMM yyyy');
        case 'quarterly':
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        default:
            return fallback;
    }
};

const VisitorOnboardingSplitChart: React.FC<Props> = ({ data = [], period }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const anonymous: number[] = [];
            const registered: number[] = [];
            const profileComplete: number[] = [];
            const totalsByIndex: number[] = [];

            data.forEach((item) => {
                let date: Date;
                try {
                    date = item.period_start ? parseISO(item.period_start) : new Date();
                    if (isNaN(date.getTime())) {
                        date = new Date(item.period_start || '');
                    }
                } catch {
                    date = new Date();
                }

                const anonymousCount = Number(item.anonymous_visitors) || 0;
                const registeredCount = Number(item.registered_visitors) || 0;
                const profileCompleteCount = Number(item.profile_complete_visitors) || 0;
                const total = Number(item.total_visitors) || anonymousCount + registeredCount + profileCompleteCount;

                categories.push(formatPeriodLabel(date, period, item.period || ''));
                anonymous.push(anonymousCount);
                registered.push(registeredCount);
                profileComplete.push(profileCompleteCount);
                totalsByIndex.push(total);
            });

            const formatBarLabel = (params: any) => {
                const total = totalsByIndex[params.dataIndex] || 0;
                const value = Number(params.value) || 0;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${value.toLocaleString()}\n${percentage}%`;
            };

            const option: ECOption = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: (params: any) => {
                        if (!Array.isArray(params)) {
                            return '';
                        }

                        const index = params[0]?.dataIndex || 0;
                        const total = totalsByIndex[index] || 0;
                        const lines = [params[0]?.axisValue || ''];
                        params.forEach((param: any) => {
                            const value = Number(param.value) || 0;
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                            lines.push(`${param.marker} ${param.seriesName}: ${value.toLocaleString()} (${percentage}%)`);
                        });
                        lines.push(`Total visitors: ${total.toLocaleString()}`);
                        return lines.join('<br/>');
                    },
                },
                legend: {
                    data: ['Anonymous', 'Registered (phone only)', 'Company profile complete'],
                    bottom: 0,
                },
                grid: {
                    left: '6%',
                    right: '6%',
                    top: '12%',
                    bottom: 70,
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: { rotate: period === 'daily' ? 45 : 0 },
                },
                yAxis: {
                    type: 'value',
                    name: 'Unique visitors',
                },
                series: [
                    {
                        name: 'Anonymous',
                        type: 'bar',
                        data: anonymous,
                        itemStyle: { color: '#1890ff' },
                        barMaxWidth: 28,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Registered (phone only)',
                        type: 'bar',
                        data: registered,
                        itemStyle: { color: '#faad14' },
                        barMaxWidth: 28,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Company profile complete',
                        type: 'bar',
                        data: profileComplete,
                        itemStyle: { color: '#52c41a' },
                        barMaxWidth: 28,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 380 }} />;
};

export default VisitorOnboardingSplitChart;
